import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useUser } from '~/api/user';
import { Dialog } from '~/components';
import { AnchorTargets } from '~/constants/url';
import { routes } from '~/providers/RouterProvider/router.routes';
import { getAccountCompletionProgress } from '~/utils/user';

import { AccountCompletionCard } from './AccountCompletionCard/AccountCompletionCard';
import { AccountCompletionCheckbox } from './AccountCompletionCheckbox/AccountCompletionCheckbox';
import { ContractDialog } from './ContractDialog/ContractDialog';

type Props = {
  createInvoice: () => void;
  isCreatingInvoice: boolean;
};

export const AccountCompletion = ({ createInvoice, isCreatingInvoice }: Props) => {
  const { data: user } = useUser();

  const { t } = useTranslation(['dashboard']);

  const contractHandle = Dialog.useHandle();

  const progress = getAccountCompletionProgress(user);
  const steps = {
    accountCreated: true,
    frameworkAgreement: progress.frameworkAgreement,
    vatRegulation: progress.vatRegulation,
    financialInfo: progress.financialInfo,
    peppol: progress.peppol,
  };

  const totalSteps = Object.keys(steps).length;
  const completedSteps = Object.values(steps).filter(Boolean).length;
  const allPreviousStepsChecked = Object.values(progress).every(Boolean);

  if (user.hasSubmittedInvoice) return null;

  return (
    <section>
      <AccountCompletionCard completedSteps={completedSteps} totalSteps={totalSteps}>
        {/* Account created */}
        <AccountCompletionCheckbox checked={steps.accountCreated} label={t('dashboard:progression.steps.create')} />

        {/* Contract signed */}
        <AccountCompletionCheckbox
          checked={steps.frameworkAgreement}
          label={
            user.contractSigned && !user.addendumSigned
              ? t('dashboard:progression.steps.signAddendum2024')
              : t('dashboard:progression.steps.signContract')
          }
          render={<Dialog.Trigger handle={contractHandle} />}
        />

        {/* Vat regulation */}
        <AccountCompletionCheckbox
          checked={steps.vatRegulation}
          label={t('dashboard:progression.steps.vatRegulation')}
          render={<Link to={{ pathname: routes.settingsCompany, hash: AnchorTargets.SETTINGS_COMPANY_VAT_REGULATION }} />}
        />

        {/* Financial info */}
        <AccountCompletionCheckbox
          checked={steps.financialInfo}
          label={t('dashboard:progression.steps.iban')}
          render={<Link to={{ pathname: routes.settingsCompany, hash: AnchorTargets.SETTINGS_COMPANY_FINANCIAL }} />}
        />

        {/* Peppol ID */}
        <AccountCompletionCheckbox
          checked={steps.peppol}
          label={t('dashboard:progression.steps.peppol')}
          render={<Link to={{ pathname: routes.settingsIntegrations, hash: AnchorTargets.SETTINGS_INTEGRATIONS_PEPPOL }} />}
        />

        {/* Invoice submitted */}
        <AccountCompletionCheckbox
          checked={false}
          label={t('dashboard:progression.steps.createInvoice.title')}
          locked={!allPreviousStepsChecked}
          render={<button disabled={!allPreviousStepsChecked || isCreatingInvoice} onClick={createInvoice} />}
          text={allPreviousStepsChecked ? undefined : t('dashboard:progression.steps.createInvoice.text')}
        />
      </AccountCompletionCard>

      <Dialog.Root handle={contractHandle}>
        <ContractDialog onClose={() => contractHandle.close()} />
      </Dialog.Root>
    </section>
  );
};
